import React, { useState, useEffect } from 'react';
import Layout from "components/Layout";
import { Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { refreshAuth } from 'features/user';
import { qrAccountView } from 'features/qr/QrAccountView';
import { qrAccountCreate } from 'features/qr/QrAccountCreate';
import { createBulkQrLabelCall } from 'features/qrLabel/createBulkQrLabel';
import DatalistAltCS from './forms/DatalistAltCS';
import { viewAllQrLabelsCall } from 'features/qrLabel/viewAllQrLabels';
import { viewAllQrLabelsCallNoLoad } from 'features/qrLabel/viewAllQrLabelsNoLoad';
import { useTranslation } from 'react-i18next';
import ModalWithChildren from 'components/ModalWithChildren';
import FormDetailsMultilang from './forms/FormDetailsMultilang';
import MultiLanguageForm from 'components/MultiLanguageForm';
import TeamSidebar from 'components/TeamSidebar';

import { FaUser, FaChartBar, FaBuilding, FaPlus, FaCopy } from 'react-icons/fa';


const DashboardPage = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
        isAuthenticated,
        user,
        loading,
        qr_account,
        qr_account_created,
        qr_account_not_exist,
        qr_label_list,
        qr_label_list_no_data,
        qr_label_list_have_change,
        qr_label_list_load
    } = useSelector(state => state.user);

    const [showModal, setShowModal] = useState(false);
    const [content, setContent] = useState(0);
    const [masked_id, setMasked_id] = useState(null);
    const [data, setData] = useState([]);

    useEffect(() => {
        const refreshUser = () => {
            dispatch(refreshAuth());
        };
        const intervalId = setInterval(refreshUser, 10 * 60 * 1000);
        return () => clearInterval(intervalId);
    }, [dispatch]);

    useEffect(() => {
        if (user) {
            const payload = { id: user.id };
            dispatch(viewAllQrLabelsCall(payload));
        }
    }, [user, isAuthenticated]);

    useEffect(() => {
        if (user) {
            const payload = { id: user.id };
            dispatch(viewAllQrLabelsCallNoLoad(payload));
        }
    }, [dispatch]);

    useEffect(() => {
        if (user) {
            dispatch(qrAccountView());
        }
    }, [dispatch, user]);

    useEffect(() => {
        if (!qr_account && !qr_account_not_exist) {
            dispatch(qrAccountView());
        }
    }, [dispatch, qr_account, qr_account_not_exist, qr_account_created]);

    useEffect(() => {
        if (user && !qr_label_list && !qr_label_list_no_data) {
            const payload = { id: user.id };
            dispatch(viewAllQrLabelsCallNoLoad(payload));
        }
    }, [dispatch, qr_label_list, qr_label_list_no_data, user]);

    useEffect(() => {
        if (user && qr_label_list_have_change) {
            const payload = { id: user.id };
            dispatch(viewAllQrLabelsCallNoLoad(payload));
        }
    }, [qr_label_list_have_change, dispatch, user]);

    useEffect(() => {
        if (qr_label_list) {
            setData(qr_label_list.data);
        }
    }, [qr_label_list]);

    const showModalForm_1 = () => {
        setShowModal(true);
        setContent(1);
    };

    const showModalForm_2 = (maskedId) => {
        setShowModal(true);
        setContent(2);
        setMasked_id(maskedId);
    };

    const hideModal = () => {
        setShowModal(false);
    };

    const showWarning = () => {
        if (qr_label_list && qr_account) {
            const num = Number(qr_account.qr_count);
            const num2 = Number(qr_label_list.data.length);
            return num === num2;
        }
        return false;
    };

    const displayedContent = () => {
        if (!showModal) return;
        switch (content) {
            case 1:
                return (
                    <>
                        {showWarning() && (
                            <p style={{ color: "red" }}>
                                {t('qr_max')}
                            </p>
                        )}
                        <MultiLanguageForm
                            onSave={(payload) => {
                                if (showWarning()) return;
                                dispatch(createBulkQrLabelCall(payload));
                                setShowModal(false);
                            }}
                            isLoading={qr_label_list_load}
                        />
                    </>
                );
            case 2:
                return <FormDetailsMultilang data={data} masked_id={masked_id} close_modal={hideModal} />;
            case 3:
                return null; // no longer used, sidebar replaces modal
            default:
                setShowModal(false);
                setContent(0);
                break;
        }
    };

    const uniqueProductCount = data.length ? new Set(data.map(item => item.masked_id)).size : 0;

    if (!isAuthenticated && !loading && user === null) {
        return <Navigate to='/login' />;
    }

    // ---- RENDER ----
    return (
        <Layout title='Auth Site | Dashboard' content='Dashboard page'>
            {loading || user === null ? (
                <div className="spinner-border text-primary mt-4" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            ) : (
                <div className="dashboard-fullwidth" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)' }}>
                    {/* ====== MINT HEADER (full width) ====== */}
                    {/* ====== MINT HEADER (full width) ====== */}
                    {!qr_account_not_exist && qr_account && (
                        <div
                            style={{
                                backgroundColor: '#e6fff2',
                                padding: '20px 30px',
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'flex-start',
                                borderBottom: '1px solid #b8e6c3',
                                gap: 0,
                            }}
                        >
                            {/* USER */}
                            <div style={{ flex: '1 1 180px', paddingRight: '24px', borderRight: '1px solid #b8e6c3' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2e7d32', marginBottom: '8px' }}>
                                    <FaUser /> <span style={{ fontWeight: 600 }}>{t('user')}</span>
                                </div>
                                <div style={{ fontSize: '14px', lineHeight: '1.7' }}>
                                    <div>{user.first_name} {user.last_name}</div>
                                    <div style={{ color: '#555' }}>{user.email}</div>
                                    {/* TAG with copy */}
                                    <div style={{ color: '#777', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                                        <span>{t('TAG')}:</span>
                                        <code style={{
                                            backgroundColor: '#fff',
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            fontSize: '13px',
                                            border: '1px solid #c3e6cb',
                                        }}>
                                            {user.unique_name}
                                        </code>
                                        <button
                                            className="btn-icon-mint"
                                            onClick={() => navigator.clipboard.writeText(user.unique_name)}
                                            title={t('copy')}
                                            style={{ fontSize: '16px' }}
                                        >
                                            <FaCopy />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* QR ACCOUNT + LABEL COUNTER */}
                            <div style={{
                                flex: '1 1 220px',
                                paddingLeft: '24px',
                                paddingRight: '24px',
                                borderRight: '1px solid #b8e6c3',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2e7d32', marginBottom: '12px' }}>
                                    <FaChartBar /> <span style={{ fontWeight: 600 }}>{t('qr_account')}</span>
                                </div>
                                <div style={{ fontSize: '14px', lineHeight: '1.7' }}>
                                    {/* Created & Valid until */}
                                    <div style={{ color: '#555', fontSize: '12px', marginBottom: '8px' }}>
                                        {t('creation_date')}: {new Date(qr_account.date_created).toLocaleDateString()}
                                        <br />
                                        {t('valid_until')}: {qr_account.date_end
                                            ? new Date(qr_account.date_end).toLocaleDateString()
                                            : '—'}
                                    </div>

                                    {/* Label counter */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>

                                        <span style={{ color: '#6c757d', fontSize: '13px' }}>QR:</span>
                                        <span style={{ fontSize: '15px', fontWeight: 600, color: '#2e7d32' }}>
                                            {uniqueProductCount} / {qr_account.qr_count}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* IMPRESSUM */}
                            <div style={{ flex: '1 1 200px', paddingLeft: '24px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2e7d32', marginBottom: '8px' }}>
                                    <FaBuilding /> <span style={{ fontWeight: 600 }}>{t('impressum')}</span>
                                </div>
                                <div style={{ fontSize: '14px', lineHeight: '1.7', color: '#444' }}>
                                    <div>{qr_account.company_name}</div>
                                    <div>{qr_account.company_address}</div>
                                    <div>{qr_account.company_oib}</div>
                                    <div>{qr_account.company_phone}</div>
                                    <div>{qr_account.company_email}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ====== BODY (sidebar + content) ====== */}
                    <div style={{ display: 'flex', flex: 1 }}>
                        {!qr_account_not_exist && qr_account && <TeamSidebar />}

                        <div style={{ flex: 1, padding: '25px', backgroundColor: '#fff', overflowX: 'hidden' }}>
                            {/* No Organisation Message */}
                            {qr_account_not_exist && (
                                <div style={{
                                    maxWidth: '550px',
                                    margin: '60px auto',
                                    padding: '45px 35px',
                                    textAlign: 'center',
                                    backgroundColor: '#ffffff',
                                    borderRadius: '14px',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                                    border: '1px solid #e0e0e0',
                                }}>
                                    <h4 style={{ marginBottom: '16px', color: '#2e7d32' }}>
                                        {t('no_organisation_title')}
                                    </h4>
                                    <p style={{ fontSize: '15px', color: '#555', lineHeight: '1.6', marginBottom: '28px' }}>
                                        {t('no_organisation_message')}
                                    </p>
                                    <div style={{
                                        backgroundColor: '#e6fff2',
                                        borderRadius: '10px',
                                        padding: '18px 24px',
                                        display: 'inline-block',
                                        border: '1px solid #b8e6c3',
                                    }}>
                                        <div style={{ fontSize: '14px', color: '#2e7d32', marginBottom: '8px' }}>
                                            {t('your_tag')}
                                        </div>
                                        <code style={{
                                            fontSize: '24px',
                                            letterSpacing: '2px',
                                            backgroundColor: '#fff',
                                            padding: '6px 16px',
                                            borderRadius: '8px',
                                            border: '1px solid #d4edda',
                                        }}>
                                            {user.unique_name}
                                        </code>
                                    </div>
                                    <p style={{ marginTop: '22px', fontSize: '13px', color: '#888' }}>
                                        {t('give_tag_to_admin')}
                                    </p>
                                </div>
                            )}

                            {/* Product Grid */}
                            {/* Add new label button */}

                            {!qr_account_not_exist && qr_account && (
                                <>
                                    <div>
                                        <button
                                            className="btn-outline-mint"
                                            onClick={showModalForm_1}
                                            style={{ fontSize: '13px', padding: '6px 14px', marginBottom: "15px", marginLeft: "20px" }}
                                        >
                                            <FaPlus style={{ fontSize: '14px' }} /> {t('add')}
                                        </button>
                                    </div>

                                    <DatalistAltCS
                                        data={data}
                                        num={qr_account.qr_count}
                                        add={showModalForm_1}
                                        det={showModalForm_2}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <ModalWithChildren
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                closeOnBackdropClick={false}
                renderSave={false}
                renderDelete={false}
                style={{ maxWidth: "800px", minHeight: "600px" }}
            >
                {displayedContent()}
            </ModalWithChildren>
        </Layout>
    );
};

export default DashboardPage;